import { create, type StoreApi, type UseBoundStore } from 'zustand';
import type { ChatMessage, MessagePart, ToolCallEvent } from '../types';

export type { ChatMessage, MessagePart, ToolCallEvent };

/**
 * A dashboard proposal arriving from the chat agent's `propose_dashboard`
 * tool. Mirrors the shape of `DashboardProposal` in @bastion/dashboards
 * but kept here to avoid the assistant package depending on dashboards.
 * The webapp layer reconstructs the rich type when handing it to
 * `PreviewColumn`.
 */
export interface DashboardArtifactProposal {
  title: string;
  description?: string | null;
  /** DashboardDefinition from the dashboards package — kept loose here. */
  definition: { widgets: unknown[]; parameters?: unknown[] };
  explanation: string;
  is_public?: boolean;
}

export interface ChatState {
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  sessionId: string;

  /**
   * Latest validated dashboard proposal from `propose_dashboard` (the
   * canonical commit). The right-side artifact panel reads this to
   * render the preview. Cleared on session change / new conversation.
   */
  dashboardProposal: DashboardArtifactProposal | null;
  /**
   * In-flight widgets from `propose_widget` calls in the current turn.
   * Keyed by position so out-of-order arrivals + replacements work.
   * Combined with `dashboardProposal.widgets` to derive the live
   * preview that fills in tile-by-tile while the agent is drafting.
   */
  streamingWidgets: Map<number, unknown>;

  addUserMessage: (content: string) => string;
  addAssistantMessage: () => string;
  appendToAssistant: (messageId: string, text: string) => void;
  addToolCall: (messageId: string, toolCall: ToolCallEvent) => void;
  updateToolCallStatus: (messageId: string, toolCallId: string, status: ToolCallEvent['status']) => void;
  setToolCallConfirmation: (
    messageId: string,
    toolCallId: string,
    confirmation: NonNullable<ToolCallEvent['confirmation']>,
  ) => void;
  attachToolResult: (
    messageId: string,
    toolCallId: string,
    result: string,
    options?: { truncated?: boolean; fullLength?: number },
  ) => void;
  setStreaming: (streaming: boolean) => void;
  setError: (error: string | null) => void;
  clearMessages: (options?: { sessionId?: string }) => void;
  setSessionId: (sessionId: string) => void;
  loadMessages: (messages: ChatMessage[]) => void;
  setDashboardProposal: (proposal: DashboardArtifactProposal | null) => void;
  upsertStreamingWidget: (position: number, widget: unknown) => void;
  clearStreamingWidgets: () => void;
  clearDashboardArtifact: () => void;
}

/**
 * Build the `parts` array for a message that didn't come from the streaming
 * pipeline (e.g. loaded from history, or rendered by a consumer that pre-dates
 * the parts model). Tool calls render before text — matches how older
 * conversations always rendered. Exported so renderers can fall back to the
 * same rule and stay in lock-step with persistence.
 */
export function derivePartsFromLegacy(message: ChatMessage): MessagePart[] {
  const parts: MessagePart[] = [];
  for (const tc of message.toolCalls ?? []) {
    parts.push({ type: 'tool', toolCall: tc });
  }
  if (message.content) {
    parts.push({ type: 'text', content: message.content });
  }
  return parts;
}

export function createChatStore(): UseBoundStore<StoreApi<ChatState>> {
  let messageCounter = 0;
  function generateId(): string {
    return `msg_${Date.now()}_${++messageCounter}`;
  }

  return create<ChatState>((set) => ({
    messages: [],
    isStreaming: false,
    error: null,
    sessionId: crypto.randomUUID(),
    dashboardProposal: null,
    streamingWidgets: new Map(),

    addUserMessage: (content: string) => {
      const id = generateId();
      set((s) => ({
        messages: [
          ...s.messages,
          {
            id,
            role: 'user',
            content,
            parts: [{ type: 'text', content }],
            timestamp: Date.now(),
          },
        ],
        error: null,
      }));
      return id;
    },

    addAssistantMessage: () => {
      const id = generateId();
      set((s) => ({
        messages: [
          ...s.messages,
          {
            id,
            role: 'assistant',
            content: '',
            toolCalls: [],
            parts: [],
            timestamp: Date.now(),
          },
        ],
      }));
      return id;
    },

    appendToAssistant: (messageId: string, text: string) => {
      if (!text) return;
      set((s) => ({
        messages: s.messages.map((m) => {
          if (m.id !== messageId) return m;
          const parts = m.parts ?? [];
          const last = parts[parts.length - 1];
          const nextParts: MessagePart[] =
            last && last.type === 'text'
              ? [
                  ...parts.slice(0, -1),
                  { type: 'text', content: last.content + text },
                ]
              : [...parts, { type: 'text', content: text }];
          return { ...m, content: m.content + text, parts: nextParts };
        }),
      }));
    },

    addToolCall: (messageId: string, toolCall: ToolCallEvent) => {
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === messageId
            ? {
                ...m,
                toolCalls: [...(m.toolCalls ?? []), toolCall],
                parts: [...(m.parts ?? []), { type: 'tool', toolCall }],
              }
            : m
        ),
      }));
    },

    updateToolCallStatus: (messageId: string, toolCallId: string, status: ToolCallEvent['status']) => {
      set((s) => ({
        messages: s.messages.map((m) => {
          if (m.id !== messageId) return m;
          const toolCalls = m.toolCalls?.map((tc) =>
            tc.id === toolCallId ? { ...tc, status } : tc,
          );
          const parts = m.parts?.map<MessagePart>((part) =>
            part.type === 'tool' && part.toolCall.id === toolCallId
              ? { type: 'tool', toolCall: { ...part.toolCall, status } }
              : part,
          );
          return { ...m, toolCalls, parts };
        }),
      }));
    },

    setToolCallConfirmation: (messageId, toolCallId, confirmation) => {
      set((s) => ({
        messages: s.messages.map((m) => {
          if (m.id !== messageId) return m;
          const patch: Partial<ToolCallEvent> = {
            status: 'awaiting_confirmation',
            confirmation,
          };
          const toolCalls = m.toolCalls?.map((tc) =>
            tc.id === toolCallId ? { ...tc, ...patch } : tc,
          );
          const parts = m.parts?.map<MessagePart>((part) =>
            part.type === 'tool' && part.toolCall.id === toolCallId
              ? { type: 'tool', toolCall: { ...part.toolCall, ...patch } }
              : part,
          );
          return { ...m, toolCalls, parts };
        }),
      }));
    },

    attachToolResult: (messageId, toolCallId, result, options) => {
      const patch: Partial<ToolCallEvent> = {
        result,
        resultTruncated: options?.truncated ?? false,
        resultLength: options?.fullLength,
      };
      set((s) => ({
        messages: s.messages.map((m) => {
          if (m.id !== messageId) return m;
          const toolCalls = m.toolCalls?.map<ToolCallEvent>((tc) => {
            if (tc.id !== toolCallId) return tc;
            const nextStatus: ToolCallEvent['status'] =
              tc.status === 'awaiting_confirmation' || tc.status === 'cancelled'
                ? tc.status
                : 'executed';
            return { ...tc, ...patch, status: nextStatus };
          });
          const parts = m.parts?.map<MessagePart>((part) =>
            part.type === 'tool' && part.toolCall.id === toolCallId
              ? {
                  type: 'tool',
                  toolCall: (() => {
                    const nextStatus: ToolCallEvent['status'] =
                      part.toolCall.status === 'awaiting_confirmation' ||
                      part.toolCall.status === 'cancelled'
                        ? part.toolCall.status
                        : 'executed';
                    return { ...part.toolCall, ...patch, status: nextStatus };
                  })(),
                }
              : part,
          );
          return { ...m, toolCalls, parts };
        }),
      }));
    },

    setStreaming: (streaming: boolean) => set({ isStreaming: streaming }),

    setError: (error: string | null) => set({ error }),

    clearMessages: (options) =>
      set({
        messages: [],
        error: null,
        isStreaming: false,
        sessionId: options?.sessionId ?? crypto.randomUUID(),
        // Drop any in-flight dashboard artifact when the user starts a
        // new conversation — the proposal is per-conversation and a
        // new chat shouldn't inherit the previous chat's tile preview.
        dashboardProposal: null,
        streamingWidgets: new Map(),
      }),

    setSessionId: (sessionId: string) => set({ sessionId }),

    loadMessages: (messages: ChatMessage[]) =>
      set({
        messages: messages.map((m) =>
          m.parts ? m : { ...m, parts: derivePartsFromLegacy(m) },
        ),
        error: null,
      }),

    setDashboardProposal: (proposal) =>
      set({
        dashboardProposal: proposal,
        // Validated dashboard supersedes any in-flight stream — the
        // server already merged everything into the canonical
        // definition, so the streaming overlay would just fight the
        // committed widgets if we kept it around.
        streamingWidgets: new Map(),
      }),

    upsertStreamingWidget: (position, widget) =>
      set((s) => {
        const next = new Map(s.streamingWidgets);
        next.set(position, widget);
        return { streamingWidgets: next };
      }),

    clearStreamingWidgets: () => set({ streamingWidgets: new Map() }),

    clearDashboardArtifact: () =>
      set({ dashboardProposal: null, streamingWidgets: new Map() }),
  }));
}

export const useChatStore = createChatStore();
export const useAssistantPanelStore = createChatStore();
